import { Component } from '@theme/component';
import { isQuarterHourTimeValue } from '@theme/utilities';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} [textInput] - The text input.
 * @property {HTMLElement} [characterCount] - The character count element.
 * @property {HTMLInputElement} [dateInput] - Date part of datetime property.
 * @property {HTMLInputElement} [timeInput] - Time part of datetime property.
 * @property {HTMLInputElement} [datetimeValue] - Combined line-item property value.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {((event: Event) => void) | null} */
  #formSubmitHandler = null;

  /** @type {HTMLFormElement | null} */
  #boundForm = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindDatetimeFormGuard();
    this.#syncDatetimeValue();
  }

  disconnectedCallback() {
    this.#unbindDatetimeFormGuard();
    super.disconnectedCallback();
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  handleDatetimeChange = () => {
    const { dateInput, timeInput } = this.refs;
    if (dateInput instanceof HTMLInputElement) dateInput.setCustomValidity('');
    if (timeInput instanceof HTMLInputElement && isQuarterHourTimeValue(timeInput.value)) {
      timeInput.setCustomValidity('');
    }
    this.#syncDatetimeValue();
  };

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  /**
   * Writes a single cart/order property: `DD-MM-YYYY HH:mm` (BE/NL readable).
   */
  #syncDatetimeValue() {
    const { dateInput, timeInput, datetimeValue } = this.refs;
    if (!(datetimeValue instanceof HTMLInputElement)) return;
    if (!(dateInput instanceof HTMLInputElement) || !(timeInput instanceof HTMLInputElement)) return;

    const dateValue = dateInput.value.trim();
    const timeValue = timeInput.value.trim();

    if (!dateValue || !timeValue || !isQuarterHourTimeValue(timeValue)) {
      datetimeValue.value = '';
      return;
    }

    const parts = dateValue.split('-');
    if (parts.length !== 3) {
      datetimeValue.value = '';
      return;
    }

    const [year, month, day] = parts;
    datetimeValue.value = `${day}-${month}-${year} ${timeValue}`;
  }

  #bindDatetimeFormGuard() {
    const { datetimeValue, dateInput } = this.refs;
    if (!(datetimeValue instanceof HTMLInputElement) || !(dateInput instanceof HTMLInputElement)) return;

    const formId = dateInput.getAttribute('form');
    const form = formId
      ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId))
      : dateInput.form;

    if (!(form instanceof HTMLFormElement)) return;

    this.#boundForm = form;
    this.#formSubmitHandler = (event) => {
      this.#syncDatetimeValue();

      const { dateInput: date, timeInput: time, datetimeValue: combined } = this.refs;
      if (!(date instanceof HTMLInputElement) || !(time instanceof HTMLInputElement)) return;
      if (!(combined instanceof HTMLInputElement)) return;

      const dateFilled = Boolean(date.value);
      const timeFilled = Boolean(time.value);
      const eitherRequired = date.required || time.required;

      if (eitherRequired && (!dateFilled || !timeFilled)) {
        event.preventDefault();
        event.stopPropagation();
        const focusTarget = !dateFilled ? date : time;
        focusTarget.reportValidity?.();
        focusTarget.focus();
        return;
      }

      if ((dateFilled && !timeFilled) || (!dateFilled && timeFilled)) {
        event.preventDefault();
        event.stopPropagation();
        const wrapper = this.querySelector('[data-datetime-property]');
        const message =
          (wrapper instanceof HTMLElement && wrapper.dataset.incompleteMessage) ||
          'Please complete both date and time.';
        const focusTarget = !dateFilled ? date : time;
        focusTarget.setCustomValidity(message);
        focusTarget.reportValidity?.();
        focusTarget.focus();
        return;
      }

      if (timeFilled && !isQuarterHourTimeValue(time.value)) {
        event.preventDefault();
        event.stopPropagation();
        const picker = time.closest('quarter-hour-time');
        if (picker && typeof picker.showNotice === 'function') picker.showNotice();
        time.focus();
        return;
      }

      date.setCustomValidity('');
      time.setCustomValidity('');
    };

    form.addEventListener('submit', this.#formSubmitHandler, true);
  }

  #unbindDatetimeFormGuard() {
    if (this.#boundForm && this.#formSubmitHandler) {
      this.#boundForm.removeEventListener('submit', this.#formSubmitHandler, true);
    }

    this.#boundForm = null;
    this.#formSubmitHandler = null;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
